import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiCallError, isAbortError } from './api';
import { useAuth } from './AuthContext';

/**
 * Generic GET hook with built-in auth, loading state, error handling, and
 * manual refetch. Phase 2 deliberately avoids React Query / SWR — single user,
 * read-mostly data, mutation cadence is human (one click per minute at most).
 *
 * Pattern (no reactive params):
 *
 *   const { data, isLoading, error, refetch } =
 *     useApiQuery((token) => api.settings.employees(token));
 *
 * Pattern (reactive params — must pass them as `deps` so the hook refetches
 * when they change; otherwise navigating between e.g. `/pcos/A` and `/pcos/B`
 * silently keeps showing A's data):
 *
 *   const { data } = useApiQuery(
 *     (token) => api.rollups.cumulative(token, pcoId),
 *     [pcoId],
 *   );
 *
 * - `data` is null until the first successful fetch.
 * - `isLoading` is true on first mount and during a refetch.
 * - 401 from the API silently surfaces as `error`; the global session-clear
 *   path lives in AuthContext and runs on the next /me bootstrap.
 *
 * The `cancelled` flag prevents a stale response from a prior fetch winning
 * over a newer one (StrictMode double-mount, navigation, refetch race).
 *
 * ── Two refetch flavours, deliberately different ──────────────────────────
 *
 * A DEP CHANGE and a REFRESH are not the same event, and conflating them is
 * what produced the "silent stale wedge": navigating `/pcos/A` → `/pcos/B`
 * kept A's numbers on screen under B's heading, and if B's fetch failed they
 * stayed there indefinitely — wrong data presented as fact, no error shown.
 *
 *   depKey change  → the identity of the thing being viewed changed, so the
 *                    old data is now *someone else's* data. Clear data+error
 *                    and show a loading state. Nothing false is ever shown.
 *   focus / poll /  → same thing, newer. Keep the current data visible and
 *   refetch()        refresh underneath; a failure leaves the last-good data
 *                    up (surfaces render a "couldn't refresh" banner over it).
 *
 * Every run gets an AbortController. It is aborted when the depKey changes or
 * the component unmounts, so a superseded request releases its socket instead
 * of racing to a result nobody will read. Aborts are swallowed silently —
 * cancelling is something we asked for, never an error to show the user.
 */
export interface UseApiQueryResult<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * True only for a *genuine* change of identity — not the first run. Extracted
 * so the semantics are unit-testable without a DOM: the null sentinel means
 * "nothing fetched yet", where `data` is already null and there is nothing to
 * clear. Exported for tests.
 */
export function isGenuineDepChange(prevDepKey: string | null, nextDepKey: string): boolean {
  return prevDepKey !== null && prevDepKey !== nextDepKey;
}

export interface UseApiQueryOptions {
  /**
   * When set, refetch on this interval (ms) while the tab is visible — for
   * surfaces where data lands out-of-band (e.g. the PDF Inbox, where a foreman
   * forward arrives while Joey is staring at the list). Skipped while the tab
   * is hidden so we don't poll a backgrounded tab. Off by default.
   */
  pollMs?: number;
}

export function useApiQuery<T>(
  /**
   * Receives an AbortSignal alongside the token. Passing it on is optional —
   * `(t) => api.x(t)` still typechecks — but fetchers that forward it as
   * `api.x(t, { signal })` get their in-flight request genuinely cancelled on
   * navigation rather than merely ignored.
   */
  fetcher: (token: string, signal: AbortSignal) => Promise<T>,
  /**
   * Optional reactive deps. Changes trigger a refetch. Pass primitive ids /
   * filter strings here; we join into a stable scalar so the deps array
   * keeps a constant length (React requires that).
   */
  deps: ReadonlyArray<unknown> = [],
  options: UseApiQueryOptions = {},
): UseApiQueryResult<T> {
  const { pollMs } = options;
  const { token, status } = useAuth();
  const [data, setData] = useState<T | null>(null);
  // `isLoading` reflects "first-paint, nothing to render yet" — true only
  // until the first successful fetch. Background refetches (post-mutation,
  // tab focus, etc.) keep the existing `data` visible so the page doesn't
  // flash through a "Loading…" branch every time Joey clicks Confirm or
  // tabs away and back.
  const [isLoading, setIsLoading] = useState<boolean>(status === 'authenticated');
  const [error, setError] = useState<Error | null>(null);
  // Latest call wins. Each fetch bumps the counter; stale resolutions (where
  // the counter has moved on) drop their response on the floor.
  const callIdRef = useRef(0);
  // Pin the latest `data` in a ref so `run` can decide whether to flip
  // isLoading without listing `data` as a dep (which would re-create the
  // callback on every fetch and dirty effects downstream).
  const dataRef = useRef<T | null>(null);
  dataRef.current = data;
  // The fetcher closes over its caller's state; pin to a ref so refetch()
  // is a stable callback (no consumer re-renders from changing `fetcher`).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  // Throttle the visibility refetch so re-focusing the tab a dozen times
  // a minute doesn't fire a dozen round trips. 30s feels right for Joey's
  // "leave the tab open for hours, pop back occasionally" flow.
  const lastFetchAtRef = useRef<number>(0);
  // The request currently in flight, so a newer run (or an unmount / dep
  // change) can cancel it.
  const inFlightRef = useRef<AbortController | null>(null);
  // Previous depKey. `null` means "nothing has been fetched yet", which is
  // why a first run is never treated as a change.
  const prevDepKeyRef = useRef<string | null>(null);

  const run = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    // Latest call wins — and now the loser actually stops, rather than running
    // to completion with its result discarded.
    inFlightRef.current?.abort();
    const controller = new AbortController();
    inFlightRef.current = controller;

    const myCallId = ++callIdRef.current;
    // Only flash a loading state if we have nothing to show yet. Refetches
    // happen quietly behind the scenes. (A dep change clears `data` first, so
    // navigation lands here with nothing to show and does flash.)
    if (dataRef.current === null) setIsLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current(token, controller.signal);
      if (callIdRef.current !== myCallId) return;
      setData(result);
      lastFetchAtRef.current = Date.now();
    } catch (err) {
      // An abort is an outcome we asked for, never a failure to report. Test
      // the signal rather than the error's name: engines disagree on what a
      // cancelled fetch rejects with, but the signal cannot be wrong.
      if (controller.signal.aborted || isAbortError(err)) return;
      if (callIdRef.current !== myCallId) return;
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (inFlightRef.current === controller) inFlightRef.current = null;
      // Leave isLoading alone on an abort: whoever superseded us owns it now.
      if (callIdRef.current === myCallId && !controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [token]);

  // Stable scalar key for the reactive deps. Refetch fires when this changes.
  // JSON.stringify handles nulls + nested objects cleanly without us caring
  // about the shape of caller's deps.
  const depKey = JSON.stringify(deps);
  useEffect(() => {
    if (status !== 'authenticated') return;

    if (isGenuineDepChange(prevDepKeyRef.current, depKey)) {
      // Different subject entirely. Whatever is on screen belongs to the
      // previous one — drop it so the page can't render B's title over A's
      // numbers, and can't silently keep showing A if B's fetch fails.
      //
      // dataRef is assigned during render, so it still holds the OLD value
      // here; clear it by hand or `run` below would read it as non-null and
      // skip the loading state.
      dataRef.current = null;
      setData(null);
      setError(null);
      setIsLoading(true);
    }
    prevDepKeyRef.current = depKey;

    void run();
    // Cancel whatever is in flight when the params change or we unmount.
    return () => {
      inFlightRef.current?.abort();
    };
  }, [run, status, depKey]);

  // Refetch when the tab regains focus / becomes visible, but throttled.
  // Without throttling, every alt-tab and every click out-and-back into
  // the browser fires a fresh round trip — and on the inbox / rollup pages
  // each of those was flashing the UI through "Loading…". 30s is long
  // enough to stop the flash, short enough to keep numbers fresh.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const REFRESH_THROTTLE_MS = 30_000;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchAtRef.current < REFRESH_THROTTLE_MS) return;
      void run();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [run, status]);

  // Optional interval polling (opt-in via options.pollMs). Only fires while
  // the tab is visible; the focus/visibility effect above covers the
  // tab-away-and-back case. Background refetches keep `data` visible, so the
  // list never flashes "Loading…". `lastFetchAtRef` is shared with the
  // visibility refetch, so a recent focus-refetch defers the next poll tick.
  useEffect(() => {
    if (status !== 'authenticated' || !pollMs) return;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchAtRef.current < pollMs) return;
      void run();
    }, pollMs);
    return () => clearInterval(id);
  }, [run, status, pollMs]);

  return { data, isLoading, error, refetch: run };
}

/**
 * Re-export so callers can do `instanceof ApiCallError` against the `error`
 * value without a separate import.
 */
export { ApiCallError };
